import React, { useContext, useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { ImageUsageResponse } from '../../Models/Models';
import DiscardButton from '../Buttons/DiscardButton';
import DeleteButton from '../Buttons/DeleteButton';
import { Link } from 'react-router-dom';
import CustomTooltip from '../Tooltips/CustomTooltip';
import { ImageService } from '../../Services/ImageService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRefresh, faTrash, faTriangleExclamation, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { SiteContext, ToastContext } from '../../Contexts';

interface Props {
    show: boolean;
    onHide: () => void;
    onSubmit: () => void;
    idToDelete: number;
}

const ImageDeleteModal: React.FC<Props> = ({ show, onHide, onSubmit, idToDelete }) => {
    const { checkImageUsage } = ImageService();
    const [usageData, setUsageData] = useState<ImageUsageResponse | null>(null);
    const { setToast }: any = useContext(ToastContext);
    const { currentSite }: any = useContext(SiteContext);
    const [showHelper, setShowHelper] = useState(false);

    const load = async (initCall: boolean) => {
        setUsageData(await checkImageUsage(idToDelete));
        if (!initCall) setToast({ message: 'Použitia obrázku úspešne obnovené' });
    };

    useEffect(() => {
        if (idToDelete === 0) return;
        void load(true);
    }, [idToDelete]);

    const isBlocked = usageData?.blockDelete ?? false;
    const usageCount = usageData
        ? usageData.posts.length + usageData.galleries.length + usageData.titleImageGalleries.length + usageData.pageImagePositions.length
        : 0;

    return (
        <>
            <Modal show={show} onHide={onHide} centered>
                <div className="st-delete-modal-stripe" />
                <Modal.Header className="st-delete-modal-header">
                    <div className="st-delete-modal-header-inner">
                        <div className="st-delete-modal-icon-badge">
                            <FontAwesomeIcon icon={faTrash} className="st-delete-modal-icon" />
                        </div>
                        <span className="st-delete-modal-title">Zmazať obrázok?</span>
                    </div>
                </Modal.Header>

                <Modal.Body className="st-delete-modal-body">
                    {!usageData ? (
                        <p className="text-secondary mb-0">Načítavam...</p>
                    ) : (
                        <>
                            {usageCount === 0 ? (
                                <p className="st-delete-modal-description">
                                    Naozaj chcete zmazať tento obrázok? Túto operáciu nie je možné vrátiť naspäť.
                                </p>
                            ) : (
                                <>
                                    {(usageData.posts.length + usageData.galleries.length) > 0 && (
                                        <>
                                            <p className="st-delete-modal-text-gap">
                                                Naozaj chcete zmazať tento obrázok? Je použitý tu:
                                            </p>
                                            <ul className="st-delete-modal-usage-list">
                                                {usageData.posts.map(post => (
                                                    <li key={post.id}>
                                                        <Link target="_blank"
                                                            to={`/post/edit/${post.id}?websiteId=${currentSite.id}`}
                                                            className="st-link-blue">
                                                            Kategória: {post.name}
                                                        </Link>
                                                    </li>
                                                ))}
                                                {usageData.galleries.map(gal => (
                                                    <li key={gal.id}>
                                                        <Link target="_blank"
                                                            to={`/gallery/edit/${gal.id}?websiteId=${currentSite.id}`}
                                                            className="st-link-blue">
                                                            Galéria: {gal.id}
                                                        </Link>
                                                    </li>
                                                ))}
                                            </ul>
                                        </>
                                    )}

                                    {(usageData.titleImageGalleries.length + usageData.pageImagePositions.length) > 0 && (
                                        <div className="st-delete-modal-warning-box">
                                            <div className="st-delete-modal-warning-inner">
                                                <FontAwesomeIcon icon={faTriangleExclamation}
                                                    className="st-delete-modal-warning-icon" />
                                                <div>
                                                    <p className="st-delete-modal-warning-title">
                                                        Zmazanie nie je možné - obrázok je použitý tu:
                                                    </p>
                                                    <ul className="st-delete-modal-warning-list">
                                                        {usageData.titleImageGalleries.map(gal => (
                                                            <li key={`gallery-${gal.id}`}>
                                                                <Link target="_blank"
                                                                    to={`/gallery/edit/${gal.id}?websiteId=${currentSite.id}`}
                                                                    className="st-link-red">
                                                                    Titulný obrázok galérie: {gal.name}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                        {usageData.pageImagePositions.map(position => (
                                                            <li key={`position-${position.id}`}>
                                                                <Link target="_blank"
                                                                    to={`/image/select/${position.id}?websiteId=${currentSite.id}`}
                                                                    className="st-link-red">
                                                                    Stránka: {position.name}
                                                                </Link>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowHelper(true)}
                                                        className="st-link-button-red"
                                                    >
                                                        Ako to vyriešiť?
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </Modal.Body>

                <Modal.Footer className="st-delete-modal-footer">
                    {usageCount > 0 && (
                        <CustomTooltip placement="bottom" message="Obnoviť použitia obrázku">
                            <Button variant="light" className="st-btn-refresh" onClick={() => load(false)}>
                                <FontAwesomeIcon icon={faRefresh} />
                            </Button>
                        </CustomTooltip>
                    )}
                    <DiscardButton onClick={onHide} />
                    {isBlocked ? (
                        <CustomTooltip placement="bottom" message="Zmazanie nie je možné - obrázok je stále používaný">
                            <div>
                                <DeleteButton disabled onClick={onSubmit} />
                            </div>
                        </CustomTooltip>
                    ) : (
                        <DeleteButton onClick={onSubmit} />
                    )}
                </Modal.Footer>
            </Modal>

            <Modal show={showHelper} onHide={() => setShowHelper(false)} centered>
                <div className="st-delete-modal-stripe st-delete-modal-stripe--info" />
                <Modal.Header className="st-delete-modal-header">
                    <div className="st-delete-modal-header-inner">
                        <div className="st-delete-modal-icon-badge st-delete-modal-icon-badge--info">
                            <FontAwesomeIcon icon={faCircleInfo} className="st-delete-modal-icon st-delete-modal-icon--info" />
                        </div>
                        <span className="st-delete-modal-title">Postup zmazania obrázka</span>
                    </div>
                </Modal.Header>
                <Modal.Body className="st-help-modal-body">
                    <p className="st-help-text-danger">
                        Obrázky použité ako titulný obrázok galérie nie je možné zmazať, pretože galéria potrebuje mať titulný obrázok.
                    </p>
                    <p className="st-help-text st-help-text--gap">
                        Ak chcete situáciu vyriešiť, kliknite na príslušnú galériu a:
                    </p>
                    <ul className="st-help-list">
                        <li>vyberte nový titulný obrázok, alebo</li>
                        <li>zmažte celú galériu</li>
                    </ul>
                    <p className="st-help-text-danger st-help-text--gap mt-3">
                        Obrázky priamo vložené do stránky nie je možné zmazať, kým sú na stránke použité.
                    </p>
                    <p className="st-help-text st-help-text--gap">
                        Ak chcete situáciu vyriešiť, kliknite na odkaz „Stránka: ...“ vyššie - prejdete rovno na výber nového obrázka pre dané miesto na stránke.
                    </p>
                </Modal.Body>
                <Modal.Footer className="st-delete-modal-footer">
                    <Button variant="outline-secondary" onClick={() => setShowHelper(false)}>Zavrieť</Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default ImageDeleteModal;
